import moment, { Moment } from 'moment-timezone';

/**
 * A subscription-date range as produced by the MUI date range pickers.
 * `[null, null]` means the filter is off.
 */
export type SubscribedDateRange = [Moment | null, Moment | null];

export const EMPTY_SUBSCRIBED_DATE_RANGE: SubscribedDateRange = [null, null];

/**
 * The filter is only active when both ends of the range are set.
 */
export const isSubscribedDateRangeActive = (range: SubscribedDateRange): range is [Moment, Moment] =>
    range[0] !== null && range[1] !== null;

/**
 * Pickers return moments at local midnight. Re-anchor the calendar day in the report timezone so
 * "1 Feb" means the whole of 1 Feb where the report is being viewed, not where the browser is.
 */
const dayInTimezone = (date: Moment, timezone: string, edge: 'start' | 'end'): Moment => {
    const anchored = moment.tz(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', timezone);

    return edge === 'start' ? anchored.startOf('day') : anchored.endOf('day');
};

/**
 * Keep only users whose subscription date falls within the range, inclusive of whole boundary days
 * in the given timezone. When the range is inactive the input list is returned as-is. Users without
 * a valid subscription date are dropped when the filter is active.
 */
export const filterUsersBySubscribedAtRange = <T extends { subscribedAt?: Date | string | null }>(
    data: T[] | undefined,
    range: SubscribedDateRange,
    timezone: string,
): T[] => {
    if (!data) {
        return [];
    }

    if (!isSubscribedDateRangeActive(range)) {
        return data;
    }

    const from = dayInTimezone(range[0], timezone, 'start');
    const to = dayInTimezone(range[1], timezone, 'end');

    return data.filter(user => {
        if (user.subscribedAt === null || typeof user.subscribedAt === 'undefined') {
            return false;
        }

        const subscribedAt = moment(user.subscribedAt);

        return subscribedAt.isValid() && subscribedAt.isBetween(from, to, undefined, '[]');
    });
};

/**
 * Human-readable label for an active range, e.g. "02/01/2026 – 02/28/2026". Empty when inactive.
 * Pass a moment format such as 'l' for a shorter label on narrow screens.
 */
export const formatSubscribedDateRange = (range: SubscribedDateRange, format: string = 'L'): string => {
    if (!isSubscribedDateRangeActive(range)) {
        return '';
    }

    return `${range[0].format(format)} – ${range[1].format(format)}`;
};
